// StatusBadge.jsx
import { motion } from 'framer-motion';
import {
  Clock,
  Play,
  Pause,
  CheckCircle,
  RotateCcw,
  XCircle,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';

/**
 * Inline-style StatusBadge
 * - Accepts: status (string), size, showIcon, animated, className
 * - Also exports: PriorityBadge, Badge
 */

const STATUS_CONFIG = {
  not_started: {
    label: 'Not Started',
    color: '#6b7280',
    icon: Clock,
    bg: 'var(--gray-100, #f3f4f6)',
    text: 'var(--gray-700, #374151)',
    border: 'rgba(15, 23, 42, 0.04)'
  },
  in_progress: {
    label: 'In Progress',
    color: '#2563eb',
    icon: Play,
    bg: 'var(--blue-100, #dbeafe)',
    text: 'var(--blue-700, #1d4ed8)',
    border: 'rgba(59,130,246,0.12)'
  },
  on_hold: {
    label: 'On Hold',
    color: '#b45309',
    icon: Pause,
    bg: 'var(--yellow-100, #fef3c7)',
    text: 'var(--yellow-700, #92400e)',
    border: 'rgba(249,115,22,0.08)'
  },
  completed: {
    label: 'Completed',
    color: '#059669',
    icon: CheckCircle,
    bg: 'var(--green-100, #dcfce7)',
    text: 'var(--green-700, #047857)',
    border: 'rgba(16,185,129,0.08)'
  },
  reopened: {
    label: 'Reopened',
    color: '#ea580c',
    icon: RotateCcw,
    bg: 'var(--orange-100, #fff7ed)',
    text: 'var(--orange-700, #c2410c)',
    border: 'rgba(249,115,22,0.08)'
  },
  reclosed: {
    label: 'Reclosed',
    color: '#059669',
    icon: CheckCircle,
    bg: 'var(--green-100, #dcfce7)',
    text: 'var(--green-700, #047857)',
    border: 'rgba(16,185,129,0.08)'
  },
  cancelled: {
    label: 'Cancelled',
    color: '#dc2626',
    icon: XCircle,
    bg: 'var(--red-100, #fee2e2)',
    text: 'var(--red-700, #b91c1c)',
    border: 'rgba(239,68,68,0.08)'
  },
  escalated: {
    label: 'Escalated',
    color: '#7c3aed',
    icon: ArrowUp,
    bg: 'var(--purple-100, #f3e8ff)',
    text: 'var(--purple-700, #6d28d9)',
    border: 'rgba(124,58,237,0.08)'
  }
};

const PRIORITY_CONFIG = {
  low: {
    label: 'Low',
    icon: ArrowDown,
    bg: 'var(--green-100, #dcfce7)',
    text: 'var(--green-700, #047857)',
    border: 'rgba(16,185,129,0.08)'
  },
  medium: {
    label: 'Medium',
    icon: Minus,
    bg: 'var(--yellow-100, #fef3c7)',
    text: 'var(--yellow-700, #92400e)',
    border: 'rgba(249,115,22,0.08)'
  },
  high: {
    label: 'High',
    icon: ArrowUp,
    bg: 'var(--orange-100, #fff7ed)',
    text: 'var(--orange-700, #c2410c)',
    border: 'rgba(249,115,22,0.08)'
  },
  critical: {
    label: 'Critical',
    icon: AlertTriangle,
    bg: 'var(--red-100, #fee2e2)',
    text: 'var(--red-700, #b91c1c)',
    border: 'rgba(239,68,68,0.08)'
  }
};

const SIZE_MAP = {
  xs: { padding: '4px 8px', fontSize: 11, iconSize: 12, gap: 6 },
  sm: { padding: '6px 10px', fontSize: 12, iconSize: 14, gap: 8 },
  md: { padding: '8px 12px', fontSize: 13, iconSize: 16, gap: 8 },
  lg: { padding: '10px 14px', fontSize: 15, iconSize: 18, gap: 10 }
};

const containerBase = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 9999,
  fontWeight: 600,
  lineHeight: 1,
  borderStyle: 'solid'
};

const StatusBadge = ({
  status,
  size = 'md',
  showIcon = true,
  animated = true,
  className = ''
}) => {
  // Normalize status to lowercase for lookup (support both uppercase and lowercase)
  const normalizedStatus = status?.toLowerCase();
  
  // Support both status and priority
  const config = STATUS_CONFIG[normalizedStatus] || PRIORITY_CONFIG[normalizedStatus];

  if (!config) {
    // Unknown badge fallback
    const sz = SIZE_MAP[size] || SIZE_MAP.md;
    return (
      <span
        style={{
          ...containerBase,
          padding: sz.padding,
          fontSize: sz.fontSize,
          background: 'var(--gray-100, #f3f4f6)',
          color: 'var(--gray-700, #374151)',
          borderColor: 'rgba(15,23,42,0.04)'
        }}
        className={className}
      >
        Unknown
      </span>
    );
  }

  const Icon = config.icon;
  const sz = SIZE_MAP[size] || SIZE_MAP.md;

  const style = {
    ...containerBase,
    padding: sz.padding,
    fontSize: sz.fontSize,
    gap: sz.gap,
    background: config.bg,
    color: config.text,
    borderColor: config.border,
    boxShadow: 'inset 0 0 0 0 rgba(0,0,0,0)',
    WebkitFontSmoothing: 'antialiased'
  };

  const content = (
    <span style={style} className={className}>
      {showIcon && Icon && (
        <Icon
          style={{
            width: sz.iconSize,
            height: sz.iconSize,
            color: config.text,
            flexShrink: 0
          }}
          aria-hidden
        />
      )}
      <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>{config.label}</span>
    </span>
  );

  if (animated) {
    return (
      <motion.span
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.18 }}
        whileHover={{ scale: 1.04 }}
        style={{ display: 'inline-block' }}
      >
        {content}
      </motion.span>
    );
  }

  return content;
};

// PriorityBadge is an alias that passes priority string into StatusBadge
export const PriorityBadge = ({ priority, ...props }) => {
  return <StatusBadge status={priority} {...props} />;
};

// Generic Badge component for other variants
export const Badge = ({
  children,
  variant = 'default',
  size = 'md',
  className = '',
  ...props
}) => {
  const variants = {
    default: { bg: 'var(--gray-100, #f3f4f6)', text: 'var(--gray-700, #374151)', border: 'rgba(15,23,42,0.04)' },
    primary: { bg: 'var(--primary-100, #dbeafe)', text: 'var(--primary-700, #1d4ed8)', border: 'rgba(59,130,246,0.12)' },
    success: { bg: 'var(--green-100, #dcfce7)', text: 'var(--green-700, #047857)', border: 'rgba(16,185,129,0.08)' },
    warning: { bg: 'var(--yellow-100, #fef3c7)', text: 'var(--yellow-700, #92400e)', border: 'rgba(249,115,22,0.08)' },
    error: { bg: 'var(--red-100, #fee2e2)', text: 'var(--red-700, #b91c1c)', border: 'rgba(239,68,68,0.08)' },
    info: { bg: 'var(--blue-100, #dbeafe)', text: 'var(--blue-700, #1d4ed8)', border: 'rgba(59,130,246,0.12)' }
  };

  const v = variants[variant] || variants.default;
  const sz = SIZE_MAP[size] || SIZE_MAP.md;

  const style = {
    ...containerBase,
    padding: sz.padding,
    fontSize: sz.fontSize,
    background: v.bg,
    color: v.text,
    borderColor: v.border
  };

  return (
    <span style={style} className={className} {...props}>
      {children}
    </span>
  );
};

export default StatusBadge;
